import React, { useMemo, useState } from "react";
import { Modal, Tag } from "antd";
import { ChecklistAnswerType } from "../../../MasterData/checklistTypes";
import { getFullAwsUrl } from "../../../../../helpers/utils";
import CommonFilePreviewModal from "../../../../Components/CommonFilePreviewModal";

interface Props {
  visible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  selectedLoadData: any;
}

const ViewDispatchChecklistModal: React.FC<Props> = ({
  visible,
  setIsModalVisible,
  selectedLoadData,
}) => {
  const [previewFiles, setPreviewFiles] = useState<any[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  const dispatchChecklist = useMemo(() => {
    return selectedLoadData?.dispatch_checklist || [];
  }, [selectedLoadData]);

  const allQuestions = useMemo(() => {
    return dispatchChecklist.flatMap((c: any) => c.questions_data || []);
  }, [dispatchChecklist]);

  const getFileNameFromUrl = (key: string) => {
    try {
      const parts = key.split("/");
      const fullName = parts[parts.length - 1];
      if (fullName.includes("_") && fullName.length > 37) {
        return fullName.substring(fullName.indexOf("_") + 1);
      }
      return fullName;
    } catch (e) {
      return "File";
    }
  };

  const renderFileTags = (files: string[]) => {
    if (!Array.isArray(files) || files.length === 0) return null;
    return (
      <div className="d-flex flex-wrap gap-2 mt-1">
        {files.map((fileKey: string, idx: number) => (
          <Tag
            key={idx}
            className="d-inline-flex align-items-center px-3 py-1 bg-white border rounded-2 fs-6 fw-semibold cursor-pointer text-wrap"
            style={{
              border: "1px solid #E0E0E0",
              whiteSpace: "normal",
              wordBreak: "break-all",
              maxWidth: "100%",
              height: "auto",
            }}
            onClick={() => {
              setPreviewFiles(files);
              setPreviewIndex(idx);
              setPreviewModalOpen(true);
            }}
          >
            <span title={getFileNameFromUrl(fileKey)}>
              {getFileNameFromUrl(fileKey)}
            </span>
          </Tag>
        ))}
      </div>
    );
  };

  const renderQuestionAnswer = (q: any) => {
    const ansType = q.ans_type;
    const answer = q.answer;
    const conditionalVal = q.conditional_answer;
    const photos = q.photo || [];

    return (
      <div>
        {/* Main Answer */}
        <div className="mb-2">
          {ansType === "YES_NO" || ansType === ChecklistAnswerType.YES_NO ? (
            <div>
              <span className="fw-semibold text-secondary me-2">Answer:</span>
              <span className="fw-bold text-dark fs-6">
                {answer === "YES" || answer === true
                  ? "Yes"
                  : answer === "NO" || answer === false
                  ? "No"
                  : "-"}
              </span>
            </div>
          ) : ansType === "FILE_UPLOAD" ||
            ansType === ChecklistAnswerType.FILE_UPLOAD ? (
            <div>
              <span className="fw-semibold text-secondary d-block mb-1">
                Uploaded Files:
              </span>
              {Array.isArray(answer) && answer.length > 0 ? (
                renderFileTags(answer)
              ) : (
                <span className="fw-bold text-dark fs-6">-</span>
              )}
            </div>
          ) : (
            <div>
              <span className="fw-semibold text-secondary me-2">Answer:</span>
              <span className="fw-bold text-dark fs-6">
                {answer !== null && answer !== undefined && answer !== ""
                  ? String(answer)
                  : "-"}
              </span>
            </div>
          )}
        </div>

        {/* Conditional Answer */}
        {conditionalVal !== undefined &&
          conditionalVal !== null &&
          conditionalVal !== "" && (
            <div className="mb-2">
              <span className="fw-semibold text-secondary d-block mb-1">
                Details:
              </span>
              {Array.isArray(conditionalVal) ? (
                renderFileTags(conditionalVal)
              ) : (
                <span className="fw-bold text-dark fs-6">
                  {String(conditionalVal)}
                </span>
              )}
            </div>
          )}

        {/* Required Photos / Additional Photos */}
        {Array.isArray(photos) && photos.length > 0 && (
          <div className="mt-2">
            <span className="fw-semibold text-secondary d-block mb-1">
              Photos:
            </span>
            {renderFileTags(photos)}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        visible={visible}
        title={`Load ${selectedLoadData?.load_number || ""} Dispatch Checklist Details`}
        onCancel={() => setIsModalVisible(false)}
        footer={
          <div className="d-flex justify-content-center">
            <button
              type="button"
              className="btn btn-lg btn-outline-secondary min-w-130"
              onClick={() => setIsModalVisible(false)}
            >
              CLOSE
            </button>
          </div>
        }
        centered
        width={"55rem"}
        bodyStyle={{ maxHeight: "65vh", overflowY: "auto" }}
      >
        <div className="d-flex flex-column gap-3 p-2">
          {allQuestions?.length > 0 ? (
            allQuestions.map((q: any, idx: number) => {
              const qId = q.question_id || q._id || idx;
              return (
                <div key={qId} className="card border shadow-none rounded-3 p-3">
                  <div className="fw-bold fs-6 text-dark mb-2">
                    {idx + 1}. {q.question_text}
                  </div>
                  {renderQuestionAnswer(q)}
                </div>
              );
            })
          ) : (
            <div className="text-center text-muted py-3">
              No checklist details available.
            </div>
          )}
        </div>
      </Modal>

      {previewModalOpen && (
        <CommonFilePreviewModal
          visible={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFiles([]);
            setPreviewIndex(0);
          }}
          files={previewFiles}
          activeIndex={previewIndex}
          onActiveIndexChange={setPreviewIndex}
        />
      )}
    </>
  );
};

export default ViewDispatchChecklistModal;
